import { useTranslation } from 'react-i18next';
import type { PolicyDtoStatusEnumKey } from 'korint-sdk';
import { StatusPill } from '../ui/status-pill';

// Mirrors PENDING_STOP_STATUSES: the statuses a STOP branch may be opened on.
const ACTIVE_STATUSES: PolicyDtoStatusEnumKey[] = [
  'POLICY_SIGNED',
  'POLICY_STARTED',
  'POLICY_SUSPENDED',
];

/**
 * A stop recorded on the policy that has not run yet. Mirrors resolveStopPendingUntil, which the
 * backend indexes for the filter: the branch model echoes executesAt and leaves stoppedAt unset
 * until the merge, so reading stoppedAt alone misses every stop that runs on a branch.
 */
export const hasPendingStop = (
  status: PolicyDtoStatusEnumKey,
  policy: {
    stop?: { executesAt?: string; stopDate?: string };
    stoppedAt?: string;
    isStopAwaitingValidation?: boolean;
  },
) => {
  if (!ACTIVE_STATUSES.includes(status)) {
    return false;
  }
  // A request awaiting validation never runs on its own, so its date is no deadline.
  if (policy.isStopAwaitingValidation) {
    return true;
  }
  const pendingUntil = policy.stop?.executesAt ?? policy.stop?.stopDate ?? policy.stoppedAt;
  return pendingUntil != null && new Date(pendingUntil).getTime() > Date.now();
};

export const PendingStopChip = ({
  awaitingValidation = false,
}: {
  awaitingValidation?: boolean;
} = {}) => {
  const { t } = useTranslation();
  return (
    <StatusPill
      color="bg-orange-100 text-orange-700"
      label={t(
        awaitingValidation
          ? 'policyStatus.STOP_AWAITING_VALIDATION'
          : 'policyStatus.STOP_SCHEDULED',
      )}
    />
  );
};
